import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../services/apiClient';
import type { FaenaCalificadorCatalog } from '../../services/referencias/referenciasService';
import ModalConfirmarEliminar from '../../components/modales/ModalConfirmarEliminar';

// ===============================
// MODAL DE CALIFICADOR
// ===============================

interface CalificadorFormData {
  codigo: string;
  descripcion: string;
  opciones: string[];
  orden: number;
  activo?: boolean;
}

interface ModalCalificadorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  calificador?: FaenaCalificadorCatalog | null;
}

const ModalCalificador: React.FC<ModalCalificadorProps> = ({ isOpen, onClose, onSave, calificador }) => {
  const { t } = useTranslation();
  const isEditing = !!calificador;
  const [form, setForm] = useState<CalificadorFormData>({
    codigo: '',
    descripcion: '',
    opciones: [''],
    orden: 0,
    activo: true,
  });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (calificador) {
      setForm({
        codigo: calificador.codigo,
        descripcion: calificador.descripcion,
        opciones: calificador.opciones.length > 0 ? [...calificador.opciones] : [''],
        orden: calificador.orden,
        activo: calificador.activo,
      });
    } else {
      setForm({ codigo: '', descripcion: '', opciones: [''], orden: 0, activo: true });
    }
  }, [calificador, isOpen]);

  const agregarOpcion = () => setForm(p => ({ ...p, opciones: [...p.opciones, ''] }));

  const cambiarOpcion = (i: number, valor: string) =>
    setForm(p => { const ops = [...p.opciones]; ops[i] = valor; return { ...p, opciones: ops }; });

  const eliminarOpcion = (i: number) =>
    setForm(p => ({ ...p, opciones: p.opciones.filter((_, idx) => idx !== i) }));

  const handleGuardar = async () => {
    if (!form.codigo.trim()) { toast.error(t('admin.code_required')); return; }
    if (!form.descripcion.trim()) { toast.error(t('admin.description_required')); return; }
    const opcionesLimpias = form.opciones.map(o => o.trim()).filter(Boolean);
    if (opcionesLimpias.length === 0) { toast.error(t('admin.options_required')); return; }

    try {
      setGuardando(true);

      if (isEditing && calificador) {
        const body = { descripcion: form.descripcion, opciones: opcionesLimpias, orden: form.orden, activo: form.activo };
        await apiClient.put(`/faenas/calificadores/${calificador.id}`, body);
        toast.success(t('admin.qualifier_updated'));
      } else {
        const body = { codigo: form.codigo, descripcion: form.descripcion, opciones: opcionesLimpias };
        await apiClient.post('/faenas/calificadores', body);
        toast.success(t('admin.qualifier_created'));
      }
      onSave();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('errors.save_error'));
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditing ? t('admin.edit_qualifier') : t('admin.new_qualifier')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Código */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.code')} *</label>
            <input
              type="text"
              value={form.codigo}
              onChange={e => setForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
              disabled={isEditing}
              placeholder="Ej: HUMEDAD_SUELO"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">{t('admin.code_unique_hint')}</p>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')} *</label>
            <input
              type="text"
              value={form.descripcion}
              onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
              placeholder="Ej: Humedad del suelo"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Orden — solo en edición; al crear se asigna automáticamente */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.display_order')}</label>
              <input
                type="number"
                value={form.orden}
                min={1}
                onChange={e => setForm(p => ({ ...p, orden: parseInt(e.target.value) || 1 }))}
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{t('admin.order_unique_hint')}</p>
            </div>
          )}

          {/* Activo (solo en edición) */}
          {isEditing && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="activo"
                checked={form.activo}
                onChange={e => setForm(p => ({ ...p, activo: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="activo" className="text-sm text-gray-700">{t('admin.active_in_forms')}</label>
            </div>
          )}

          {/* Opciones */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">{t('admin.options')} *</label>
              <button
                type="button"
                onClick={agregarOpcion}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + {t('admin.add_option')}
              </button>
            </div>
            <div className="space-y-2">
              {form.opciones.map((op, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={op}
                    onChange={e => cambiarOpcion(i, e.target.value)}
                    placeholder={`${t('admin.option')} ${i + 1}`}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  {form.opciones.length > 1 && (
                    <button
                      type="button"
                      onClick={() => eliminarOpcion(i)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={guardando}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleGuardar}
            disabled={guardando}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            {guardando ? t('common.saving') : isEditing ? t('common.save_changes') : t('admin.create_qualifier')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===============================
// VISTA PRINCIPAL
// ===============================

const CalificadoresView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('faenas.crear');
  const canEdit = hasPermission('faenas.actualizar');
  const canDelete = hasPermission('faenas.eliminar');

  const [calificadores, setCalificadores] = useState<FaenaCalificadorCatalog[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState<{ isOpen: boolean; calificador: FaenaCalificadorCatalog | null }>({
    isOpen: false,
    calificador: null,
  });
  const [eliminando, setEliminando] = useState<number | null>(null);
  const [calificadorAEliminar, setCalificadorAEliminar] = useState<FaenaCalificadorCatalog | null>(null);

  const cargar = async () => {
    try {
      setCargando(true);
      const res = await apiClient.get<FaenaCalificadorCatalog[]>('/faenas/calificadores?solo_activos=false');
      setCalificadores(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('errors.load_error'));
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const handleEliminar = (cal: FaenaCalificadorCatalog) => {
    setCalificadorAEliminar(cal);
  };

  const confirmarEliminar = async () => {
    if (!calificadorAEliminar) return;
    const cal = calificadorAEliminar;
    try {
      setEliminando(cal.id);
      await apiClient.delete(`/faenas/calificadores/${cal.id}`);
      toast.success(t('admin.qualifier_deleted'));
      cargar();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('admin.cannot_delete'));
    } finally {
      setEliminando(null);
      setCalificadorAEliminar(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button onClick={() => navigate('/admin')} className="hover:text-gray-700 transition-colors">
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.qualifiers')}</span>
      </div>

      {/* Botón volver */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.qualifiers_title')}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('admin.qualifiers_description')}
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setModal({ isOpen: true, calificador: null })}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('admin.new_qualifier')}
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {cargando ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <svg className="animate-spin w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            {t('common.loading')}
          </div>
        ) : calificadores.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-sm">{t('admin.no_qualifiers')}</p>
            {canCreate && (
              <button
                onClick={() => setModal({ isOpen: true, calificador: null })}
                className="mt-3 text-blue-600 text-sm hover:underline"
              >
                {t('admin.create_first')}
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-8">#</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-20">{t('common.order')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.code')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('common.description')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('admin.options')}</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-20">{t('common.status')}</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 w-28">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {calificadores.map((cal, idx) => (
                <tr key={cal.id} className={`hover:bg-gray-50 transition-colors ${!cal.activo ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{cal.orden}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 font-medium">{cal.codigo}</td>
                  <td className="px-4 py-3 text-gray-900">{cal.descripcion}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {cal.opciones.map(op => (
                        <span
                          key={op}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100"
                        >
                          {op}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      cal.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {cal.activo ? t('admin.status_active') : t('admin.status_inactive')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <button
                          onClick={() => setModal({ isOpen: true, calificador: cal })}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('common.edit')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleEliminar(cal)}
                          disabled={eliminando === cal.id}
                          className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                          title={t('common.delete')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Nota */}
      <p className="text-xs text-gray-500">
        {t('admin.qualifiers_inactive_note')}
      </p>

      <ModalCalificador
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false, calificador: null })}
        onSave={cargar}
        calificador={modal.calificador}
      />

      <ModalConfirmarEliminar
        isOpen={!!calificadorAEliminar}
        titulo={t('admin.qualifiers')}
        mensaje={calificadorAEliminar ? `${t('admin.confirm_delete_qualifier')} "${calificadorAEliminar.descripcion}"?` : ''}
        detalles={[t('admin.cannot_delete_qualifier_hint')]}
        onConfirmar={confirmarEliminar}
        onCancelar={() => setCalificadorAEliminar(null)}
        loading={eliminando !== null}
      />
    </div>
  );
};

export default CalificadoresView;
